import React, { useRef, useState } from 'react';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
// eslint-disable-next-line import/no-extraneous-dependencies
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'react-toastify';
import { PlayIcon, TrashCanIcon } from '../../assets/svgs/icons';
import Button from '../common/Button';
import LoadingSpinner from '../common/LoadingSpinner';
import { EventMediaInterface } from '../../types/EventSettingsInterface';

export const MAX_EVENT_IMAGES = 10;
export const MAX_EVENT_VIDEOS = 1;

interface EventMediaItemProps {
  item: EventMediaInterface;
  onDelete: Function;
  disabled: boolean;
}

const EventMediaItem = ({ item, onDelete, disabled }: EventMediaItemProps) => {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: item.eventMediaID.toString()
  });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`event-media-item ${isDragging ? 'is-dragging' : ''}`}
      /* eslint-disable-next-line react/jsx-props-no-spreading */
      {...attributes}
      /* eslint-disable-next-line react/jsx-props-no-spreading */
      {...listeners}
    >
      {item.mediaType === 'video' ? (
        <div className="event-media-thumb">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video className="event-media-preview" src={item.mediaUrl} />
          <PlayIcon />
        </div>
      ) : (
        <img className="event-media-preview" src={item.mediaUrl} alt={item.altText || 'Event media'} />
      )}
      {!disabled && <TrashCanIcon width="12" height="12" onIconClicked={() => onDelete(item.eventMediaID)} />}
    </div>
  );
};

interface EventMediaUploaderProps {
  media: EventMediaInterface[];
  onUpload: Function;
  onDelete: Function;
  onReorder: Function;
}

const EventMediaUploader = ({ media, onUpload, onDelete, onReorder }: EventMediaUploaderProps) => {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const imageCount = media.filter((item) => item.mediaType === 'image').length;
  const videoCount = media.filter((item) => item.mediaType === 'video').length;

  const run = async (action: () => Promise<void>, errorMessage: string) => {
    setBusy(true);
    try {
      await action();
    } catch (error) {
      toast.error(errorMessage, { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setBusy(false);
    }
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const oldIndex = media.findIndex((item) => item.eventMediaID.toString() === active.id);
    const newIndex = media.findIndex((item) => item.eventMediaID.toString() === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    run(() => onReorder(arrayMove(media, oldIndex, newIndex)), 'Could not save the new order.');
  };

  const handleImagesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    const remaining = MAX_EVENT_IMAGES - imageCount;
    if (files.length > 0) {
      if (files.length > remaining) {
        toast.error(`You can add up to ${MAX_EVENT_IMAGES} photos.`, { position: toast.POSITION.TOP_RIGHT });
      }
      run(() => onUpload(files.slice(0, remaining), null), 'Could not upload photos.');
    }
    // reset so selecting the same file again re-triggers onChange
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const handleVideoSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      run(() => onUpload([], file), 'Could not upload video.');
    }
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  return (
    <div className="event-media-uploader">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={media.map((item) => item.eventMediaID.toString())} strategy={rectSortingStrategy}>
          <div className="event-media-grid">
            {media.map((item) => (
              <EventMediaItem
                key={item.eventMediaID}
                item={item}
                onDelete={(id: number) => run(() => onDelete(id), 'Could not delete media.')}
                disabled={busy}
              />
            ))}
            {media.length === 0 && <p className="event-media-empty">No photos or video added yet.</p>}
          </div>
        </SortableContext>
      </DndContext>

      <div className="event-media-actions">
        <input ref={imageInputRef} type="file" accept="image/*" multiple hidden onChange={handleImagesSelected} />
        <input ref={videoInputRef} type="file" accept="video/*" hidden onChange={handleVideoSelected} />
        <Button
          className="event-media-add-button"
          isDisabled={busy || imageCount >= MAX_EVENT_IMAGES}
          onClick={() => imageInputRef.current?.click()}
        >
          {`+ Add photos (${imageCount}/${MAX_EVENT_IMAGES})`}
        </Button>
        <Button
          className="event-media-add-button"
          isDisabled={busy || videoCount >= MAX_EVENT_VIDEOS}
          onClick={() => videoInputRef.current?.click()}
        >
          {`+ Add video (${videoCount}/${MAX_EVENT_VIDEOS})`}
        </Button>
        {busy && (
          <span className="event-media-busy">
            <LoadingSpinner size={22} />
          </span>
        )}
      </div>
    </div>
  );
};

export default EventMediaUploader;
